import {
  collection,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  getDocs,
  setDoc,
} from "firebase/firestore";
import { firestore } from "../../../src/firebase"; // Updated import path

// We no longer use or export getRandomTenantId

// Export handleUserProfile function
export const handleUserProfile = async (user, randomTenantId) => {
  // 'randomTenantId' is no longer used, but we keep it to avoid breaking Login.js
  try {
    const userProfileRef = doc(firestore, "UserProfileMaster", user.uid);
    const userProfileQuery = query(
      collection(firestore, "UserProfileMaster"),
      where("email", "==", user.email)
    );
    const userProfileSnapshot = await getDocs(userProfileQuery);

    const isSuperAdmin = [
      process.env.REACT_APP_SUPERADMIN_EMAIL1,
      process.env.REACT_APP_SUPERADMIN_EMAIL2,
      process.env.REACT_APP_SUPERADMIN_EMAIL3
    ].includes(user.email);

    if (isSuperAdmin) {
      // THIS IS A CRITICAL FIX:
      // It stamps the superadmin's own profile with 'createdBy'
      // so they can read their own profile.
      await setDoc(userProfileRef, {
        uid: user.uid,
        displayName: user.displayName || '',
        email: user.email,
        photoURL: user.photoURL || '',
        role: 'superadmin',
        tenantId: null,
        createdAt: new Date(),
        createdBy: user.uid // <-- THIS LINE IS ESSENTIAL
      }, { merge: true }); // Use merge: true to not overwrite data
      return 'superadmin';
    }

    // Check if user exists and handle accordingly
    if (!userProfileSnapshot.empty) {
      const existingUserData = userProfileSnapshot.docs[0].data();
      return existingUserData.role;
    }

    // Create new user profile (e.g., for a customer)
    await setDoc(userProfileRef, {
      uid: user.uid,
      displayName: user.displayName || '',
      email: user.email,
      photoURL: user.photoURL || '',
      role: 'customer',
      tenantId: null, // Customers don't get a random tenant
      createdAt: new Date()
    });
    return 'customer';
  } catch (error) {
    console.error("Error in handleUserProfile:", error);
    throw error;
  }
};

// Export fetchShops function
export const fetchShops = async () => {
  try {
    const shopsCollection = collection(firestore, "Shops");
    const shopsSnapshot = await getDocs(shopsCollection);
    return shopsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error("Error fetching shops:", error);
    return [];
  }
};

// Export createOrUpdateTenantAndAdmin function
export const createOrUpdateTenantAndAdmin = async (payload, editingAdminId = null, superAdminUid) => {
  try {
    const {
      tenantName,
      adminName,
      email,
      phoneNumber,
      tenantType,
      companyRegistrationNumber,
      gstNumber,
      headOfficeAddress,
      contactEmail,
      contactPhone,
      organisationName,
      organisationUrl,
      organisationLogoUrl,
      institutionName,
      institutionUrl,
      institutionLogoUrl,
      institutionCategory,
      customInstitutionCategory,
      affiliation,
      institutionAddress,
      institutionContactEmail,
      status,
      configuration,
    } = payload || {};

    if (!tenantName || !adminName || !email) {
      return { success: false, message: "Tenant Name, Admin Name, and Email are required." };
    }

    const normalizedTenantType =
      tenantType === "BUSINESS"
        ? "BUSINESS_VENDOR"
        : tenantType === "INSTITUTIONAL"
          ? "ORGANISATION"
          : tenantType || "BUSINESS_VENDOR";

    const role = normalizedTenantType === "ORGANISATION" ? "organisation_admin" : "admin";

    // --- LOGIC FOR UPDATING ---
    if (editingAdminId) {
      // Update user profile doc for admin
      await updateDoc(doc(firestore, "UserProfileMaster", editingAdminId), {
        email,
        phoneNumber: phoneNumber || "",
        tenantName,
        adminName,
        role,
        updatedAt: new Date(),
      });

      // Find the associated tenant to update
      const userSnapshotQuery = query(
        collection(firestore, "UserProfileMaster"),
        where("email", "==", email)
      );
      const userSnapshot = await getDocs(userSnapshotQuery);
      let tenantId = null;
      if (!userSnapshot.empty) {
        const userDoc = userSnapshot.docs[0];
        tenantId = userDoc.data().tenantId || userDoc.data().tenantid || null;
      }

      if (tenantId) {
        await updateDoc(doc(firestore, "TenantMaster", tenantId), {
          tenantName,
          adminName,
          adminEmail: email,
          adminMobile: phoneNumber || "",
          tenantType: normalizedTenantType,
          companyRegistrationNumber: companyRegistrationNumber || "",
          gstNumber: gstNumber || "",
          headOfficeAddress: headOfficeAddress || "",
          contactEmail: contactEmail || "",
          contactPhone: contactPhone || "",
          organisationName: organisationName || "",
          organisationUrl: organisationUrl || "",
          organisationLogoUrl: organisationLogoUrl || "",
          institutionName: institutionName || "",
          institutionUrl: institutionUrl || "",
          institutionLogoUrl: institutionLogoUrl || "",
          institutionCategory: institutionCategory || "SCHOOL",
          customInstitutionCategory: customInstitutionCategory || "",
          affiliation: affiliation || "",
          institutionAddress: institutionAddress || "",
          institutionContactEmail: institutionContactEmail || "",
          status: status || "ACTIVE",
          configuration: configuration || {},
          updatedAt: new Date(),
        });
      }

      return { success: true, message: "Tenant updated successfully", tenantId, adminUserId: editingAdminId };
    }

    // --- LOGIC FOR CREATING ---
    if (!superAdminUid) {
      return { success: false, message: "Error: No superadmin ID provided for creation." };
    }

    // Create new Tenant
    const tenantRef = await addDoc(collection(firestore, "TenantMaster"), {
      tenantName,
      adminName,
      adminEmail: email,
      adminMobile: phoneNumber || "",
      tenantType: normalizedTenantType,
      companyRegistrationNumber: companyRegistrationNumber || "",
      gstNumber: gstNumber || "",
      headOfficeAddress: headOfficeAddress || "",
      contactEmail: contactEmail || "",
      contactPhone: contactPhone || "",
      organisationName: organisationName || "",
      organisationUrl: organisationUrl || "",
      organisationLogoUrl: organisationLogoUrl || "",
      institutionName: institutionName || "",
      institutionUrl: institutionUrl || "",
      institutionLogoUrl: institutionLogoUrl || "",
      institutionCategory: institutionCategory || "SCHOOL",
      customInstitutionCategory: customInstitutionCategory || "",
      affiliation: affiliation || "",
      institutionAddress: institutionAddress || "",
      institutionContactEmail: institutionContactEmail || "",
      status: status || "ACTIVE",
      configuration: configuration || {},
      createdAt: new Date(),
      createdBy: superAdminUid,
    });

    // Ensure tenantId field is set on tenant doc
    await updateDoc(tenantRef, { tenantId: tenantRef.id });

    // If user already exists, promote to admin/organisation_admin
    const existingUserQuery = query(
      collection(firestore, "UserProfileMaster"),
      where("email", "==", email)
    );
    const existingUserSnapshot = await getDocs(existingUserQuery);

    if (!existingUserSnapshot.empty) {
      const existingUser = existingUserSnapshot.docs[0];
      await updateDoc(doc(firestore, "UserProfileMaster", existingUser.id), {
        tenantId: tenantRef.id,
        adminName,
        role,
        updatedAt: new Date(),
        createdBy: superAdminUid,
      });

      return {
        success: true,
        message: `Tenant added and existing user promoted to ${role}`,
        tenantId: tenantRef.id,
        adminUserId: existingUser.id,
      };
    }

    // Create new admin user profile
    const newAdminRef = await addDoc(collection(firestore, "UserProfileMaster"), {
      email,
      phoneNumber: phoneNumber || "",
      adminName,
      role,
      tenantId: tenantRef.id,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: superAdminUid,
    });

    return {
      success: true,
      message: `Tenant and ${role} user created successfully`,
      tenantId: tenantRef.id,
      adminUserId: newAdminRef.id,
    };
  } catch (error) {
    console.error("createOrUpdateTenantAndAdmin error:", error);
    return { success: false, message: "Error saving tenant/admin", error };
  }
};